import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/cms/RichTextEditor";
import BlockRenderer from "@/components/cms/BlockRenderer";
import BuilderEditor from "@/components/cms/builder/BuilderEditor";
import { ThemeTab, LayoutTab } from "@/components/cms/builder/SiteSettingsTabs";
import {
  Plus, Trash2, ArrowUp, ArrowDown, Eye, Pencil, Globe, FileText, Heading as HeadingIcon,
  Type, Image as ImageIcon, Images, MousePointerClick, Video as VideoIcon, Minus, MoveVertical,
  UploadCloud, Save, Rocket, ArrowLeft, ExternalLink, Layout, Copy,
} from "lucide-react";

const tb = (type, data) => ({ id: Math.random().toString(36).slice(2, 10), type, data: { ...DEFAULTS[type], ...(data || {}) } });

const TEMPLATES = [
  { key: "blank", label: "Page vierge", title: "", build: () => [] },
  { key: "partners", label: "Page partenaires", title: "Nos partenaires", build: () => [
      tb("heading", { text: "Nos partenaires locaux", level: "2", align: "center" }),
      tb("richtext", { html: "<p>Découvrez les commerces et professionnels partenaires de PROXIreduc-28 près de chez vous, et profitez de leurs avantages.</p>" }),
      tb("gallery", { images: [], columns: "3" }),
      tb("button", { label: "Devenir partenaire", href: "/devenir-partenaire", style: "primary", align: "center" }),
    ] },
  { key: "news", label: "Page actualité", title: "Notre actualité", build: () => [
      tb("heading", { text: "À la une", level: "2", align: "left" }),
      tb("image", { url: "", width: "full", align: "center" }),
      tb("richtext", { html: "<p>Rédigez ici votre actualité…</p>" }),
      tb("button", { label: "En savoir plus", href: "/", style: "outline", align: "left" }),
    ] },
  { key: "event", label: "Page événement", title: "Notre événement", build: () => [
      tb("heading", { text: "Nom de l'événement", level: "2", align: "center" }),
      tb("image", { url: "", width: "full", align: "center" }),
      tb("richtext", { html: "<p><strong>Date :</strong> …<br/><strong>Lieu :</strong> …<br/><strong>Horaires :</strong> …</p><p>Présentez ici votre événement, le programme et les temps forts.</p>" }),
      tb("gallery", { images: [], columns: "3" }),
      tb("button", { label: "Je m'inscris", href: "/", style: "primary", align: "center" }),
    ] },
  { key: "contact", label: "Page contact", title: "Nous contacter", build: () => [
      tb("heading", { text: "Nous contacter", level: "2", align: "left" }),
      tb("richtext", { html: "<p>Une question ? Écrivez-nous, nous vous répondrons rapidement.</p><p><strong>Email :</strong> contact@proxireduc-28.com<br/><strong>Téléphone :</strong> …<br/><strong>Adresse :</strong> …</p>" }),
      tb("button", { label: "Envoyer un message", href: "/suggestions", style: "primary", align: "left" }),
    ] },
  { key: "offers", label: "Page offres spéciales", title: "Nos offres spéciales", build: () => [
      tb("heading", { text: "Nos offres spéciales du moment", level: "2", align: "center" }),
      tb("richtext", { html: "<p>Profitez de nos offres exclusives, négociées pour vous auprès des commerces et professionnels locaux. Quantités et durées limitées !</p>", align: "center" }),
      tb("gallery", { images: [], columns: "3" }),
      tb("divider", {}),
      tb("heading", { text: "Comment en profiter ?", level: "3", align: "left" }),
      tb("richtext", { html: "<p>Créez votre compte gratuit, puis présentez votre pass chez le commerçant pour bénéficier de la réduction.</p>" }),
      tb("button", { label: "Je profite des offres — c'est gratuit", href: "/inscription", style: "primary", align: "center" }),
      tb("button", { label: "Devenir partenaire", href: "/devenir-partenaire", style: "outline", align: "center" }),
    ] },
];

const BLOCK_TYPES = [
  { type: "heading", label: "Titre", icon: HeadingIcon },
  { type: "richtext", label: "Texte riche", icon: Type },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "gallery", label: "Galerie", icon: Images },
  { type: "button", label: "Bouton / lien", icon: MousePointerClick },
  { type: "video", label: "Vidéo", icon: VideoIcon },
  { type: "divider", label: "Séparateur", icon: Minus },
  { type: "spacer", label: "Espace", icon: MoveVertical },
];

const DEFAULTS = {
  heading: { text: "Nouveau titre", level: "2", align: "left" },
  richtext: { html: "<p>Votre texte ici…</p>", align: "left" },
  image: { url: "", alt: "", caption: "", width: "full", align: "center" },
  gallery: { images: [], columns: "3" },
  button: { label: "En savoir plus", href: "/", style: "primary", align: "left" },
  video: { url: "" },
  divider: {},
  spacer: { size: 32 },
};

const uid = () => Math.random().toString(36).slice(2, 10);

// Phase 7 — pages officielles que l'admin peut remplacer par une page CMS éditable.
const OVERRIDABLE_ROUTES = [
  { route: "/", label: "Accueil" },
  { route: "/a-propos", label: "À propos" },
  { route: "/bons-plans", label: "Bons plans" },
  { route: "/partenaires", label: "Partenaires" },
];

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

function UploadBtn({ onUploaded, label = "Téléverser une image", multiple = false, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const url = await uploadImage(f);
        onUploaded(url);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={handle} data-testid={testId} />
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()} data-testid={`${testId}-btn`}>
        <UploadCloud size={15} className="mr-1.5" /> {busy ? "Envoi…" : label}
      </Button>
    </>
  );
}

function BlockEditor({ block, onChange }) {
  const d = block.data || {};
  const set = (patch) => onChange(patch);
  const inline = "h-9";

  switch (block.type) {
    case "heading":
      return (
        <div className="space-y-2">
          <Input className={inline} value={d.text || ""} onChange={(e) => set({ text: e.target.value })} placeholder="Texte du titre" data-testid="blk-heading-text" />
          <div className="flex gap-2">
            <Select value={d.level || "2"} onValueChange={(v) => set({ level: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="2">Grand titre</SelectItem><SelectItem value="3">Sous-titre</SelectItem></SelectContent>
            </Select>
            <Select value={d.align || "left"} onValueChange={(v) => set({ align: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Droite</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "richtext":
      return <RichTextEditor value={d.html} onChange={(html) => set({ html })} />;
    case "image":
      return (
        <div className="space-y-2">
          {d.url ? <img src={d.url} alt="" className="h-28 rounded-lg border border-slate-200 object-cover" /> : null}
          <div className="flex flex-wrap gap-2 items-center">
            <UploadBtn onUploaded={(url) => set({ url })} testId="blk-image-upload" />
            <Input className="flex-1 min-w-[180px] h-9" value={d.url || ""} onChange={(e) => set({ url: e.target.value })} placeholder="…ou coller une URL d'image" data-testid="blk-image-url" />
          </div>
          <Input className={inline} value={d.alt || ""} onChange={(e) => set({ alt: e.target.value })} placeholder="Texte alternatif (accessibilité / SEO)" />
          <Input className={inline} value={d.caption || ""} onChange={(e) => set({ caption: e.target.value })} placeholder="Légende (facultatif)" />
          <div className="flex gap-2">
            <Select value={d.width || "full"} onValueChange={(v) => set({ width: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="small">Petite</SelectItem><SelectItem value="medium">Moyenne</SelectItem><SelectItem value="full">Pleine largeur</SelectItem></SelectContent>
            </Select>
            <Select value={d.align || "center"} onValueChange={(v) => set({ align: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Droite</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "gallery":
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {(d.images || []).map((im, i) => (
              <div key={i} className="relative group">
                <img src={im.url} alt="" className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                <button type="button" onClick={() => set({ images: d.images.filter((_, j) => j !== i) })}
                  className="absolute -top-1.5 -right-1.5 bg-rose-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition" data-testid={`blk-gallery-remove-${i}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 items-center">
            <UploadBtn multiple label="Ajouter des images" onUploaded={(url) => onChange((cur) => ({ images: [...((cur || {}).images || []), { url, alt: "" }] }))} testId="blk-gallery-upload" />
            <Select value={d.columns || "3"} onValueChange={(v) => set({ columns: v })}>
              <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="2">2 colonnes</SelectItem><SelectItem value="3">3 colonnes</SelectItem><SelectItem value="4">4 colonnes</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "button":
      return (
        <div className="space-y-2">
          <Input className={inline} value={d.label || ""} onChange={(e) => set({ label: e.target.value })} placeholder="Libellé du bouton" data-testid="blk-button-label" />
          <Input className={inline} value={d.href || ""} onChange={(e) => set({ href: e.target.value })} placeholder="Lien (/ma-page ou https://…)" data-testid="blk-button-href" />
          <div className="flex gap-2">
            <Select value={d.style || "primary"} onValueChange={(v) => set({ style: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="primary">Plein</SelectItem><SelectItem value="outline">Contour</SelectItem></SelectContent>
            </Select>
            <Select value={d.align || "left"} onValueChange={(v) => set({ align: v })}>
              <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Gauche</SelectItem><SelectItem value="center">Centré</SelectItem><SelectItem value="right">Droite</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      );
    case "video":
      return <Input className={inline} value={d.url || ""} onChange={(e) => set({ url: e.target.value })} placeholder="Lien YouTube ou Vimeo" data-testid="blk-video-url" />;
    case "spacer":
      return (
        <div className="flex items-center gap-2">
          <Label className="text-sm">Hauteur</Label>
          <Input type="number" className="w-28 h-9" value={d.size || 32} onChange={(e) => set({ size: Number(e.target.value) })} />
          <span className="text-sm text-slate-400">px</span>
        </div>
      );
    default:
      return <p className="text-sm text-slate-400">Aucun réglage pour ce bloc.</p>;
  }
}

function PageEditor({ page, onBack, onSaved }) {
  const [form, setForm] = useState(() => ({
    title: page.title || "", slug: page.slug || "", meta_description: page.meta_description || "",
    nav_show: !!page.nav_show, nav_label: page.nav_label || "", status: page.status || "draft",
    blocks: page.blocks || [],
  }));
  const [saving, setSaving] = useState(false);
  const isNew = !page.id;

  const addBlock = (type) => setForm((f) => ({ ...f, blocks: [...f.blocks, { id: uid(), type, data: { ...DEFAULTS[type] } }] }));
  const updateBlock = (id, patch) => setForm((f) => ({
    ...f,
    blocks: f.blocks.map((b) => {
      if (b.id !== id) return b;
      const p = typeof patch === "function" ? patch(b.data || {}) : patch;
      return { ...b, data: { ...(b.data || {}), ...p } };
    }),
  }));
  const removeBlock = (id) => setForm((f) => ({ ...f, blocks: f.blocks.filter((b) => b.id !== id) }));
  const moveBlock = (id, dir) => setForm((f) => {
    const arr = [...f.blocks];
    const i = arr.findIndex((b) => b.id === id);
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, blocks: arr };
  });

  const save = async (publish) => {
    if (!form.title.trim()) { toast.error("Donnez un titre à la page."); return; }
    setSaving(true);
    try {
      const payload = { ...form, status: publish ? "published" : "draft" };
      let saved;
      if (isNew) { saved = (await api.post("/admin/cms/pages", payload)).data; }
      else { saved = (await api.put(`/admin/cms/pages/${page.id}`, payload)).data; }
      toast.success(publish ? "Page publiée ✅" : "Brouillon enregistré (hors ligne jusqu'à publication)");
      onSaved(saved);
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div data-testid="cms-page-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="cms-editor-back"><ArrowLeft size={15} /> Retour aux pages</button>
        <div className="flex items-center gap-2">
          {!isNew && page.status === "published" && (
            <a href={`/p/${form.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-emerald-700 font-semibold hover:underline" data-testid="cms-editor-view"><ExternalLink size={14} /> Voir en ligne</a>
          )}
          <Button variant="outline" size="sm" disabled={saving} onClick={() => save(false)} data-testid="cms-editor-save-draft"><Save size={15} className="mr-1.5" /> Brouillon</Button>
          <Button size="sm" disabled={saving} onClick={() => save(true)} className="bg-[#E05638] text-white" data-testid="cms-editor-publish"><Rocket size={15} className="mr-1.5" /> Publier</Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Colonne édition */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
            <div><Label>Titre de la page</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Ex. Nos partenaires" data-testid="cms-page-title-input" /></div>
            <div><Label>Adresse (slug)</Label>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-slate-400">/p/</span>
                <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="auto depuis le titre" data-testid="cms-page-slug-input" />
              </div>
            </div>
            <div><Label>Description SEO</Label><Textarea rows={2} value={form.meta_description} onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))} placeholder="Résumé affiché dans Google (max ~160 caractères)" /></div>
            <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
              <div><div className="text-sm font-semibold text-slate-800">Afficher dans le menu</div><div className="text-xs text-slate-500">Lien rapide vers cette page</div></div>
              <Switch checked={form.nav_show} onCheckedChange={(v) => setForm((f) => ({ ...f, nav_show: v }))} data-testid="cms-page-nav-toggle" />
            </div>
            {form.nav_show && <Input value={form.nav_label} onChange={(e) => setForm((f) => ({ ...f, nav_label: e.target.value }))} placeholder="Libellé dans le menu (ex. Partenaires)" />}
          </div>

          <div className="space-y-3">
            {form.blocks.map((b, i) => {
              const meta = BLOCK_TYPES.find((t) => t.type === b.type);
              const Icon = meta?.icon || Layout;
              return (
                <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={`cms-block-edit-${b.type}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500"><Icon size={14} /> {meta?.label || b.type}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveBlock(b.id, -1)} disabled={i === 0} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30" data-testid={`cms-block-up-${i}`}><ArrowUp size={14} /></button>
                      <button onClick={() => moveBlock(b.id, 1)} disabled={i === form.blocks.length - 1} className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30" data-testid={`cms-block-down-${i}`}><ArrowDown size={14} /></button>
                      <button onClick={() => removeBlock(b.id)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" data-testid={`cms-block-remove-${i}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <BlockEditor block={b} onChange={(data) => updateBlock(b.id, data)} />
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5"><Plus size={14} /> Ajouter un bloc</div>
            <div className="flex flex-wrap gap-2">
              {BLOCK_TYPES.map((t) => (
                <button key={t.type} onClick={() => addBlock(t.type)} data-testid={`cms-add-block-${t.type}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:border-[#E05638] hover:text-[#E05638] transition">
                  <t.icon size={14} /> {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Colonne aperçu */}
        <div className="lg:sticky lg:top-6 self-start">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5"><Eye size={14} /> Aperçu en direct</div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 min-h-[300px]" data-testid="cms-preview">
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900 mb-5">{form.title || "Titre de la page"}</h1>
            <div className="space-y-5">
              {form.blocks.length === 0 && <p className="text-slate-400 text-sm">Ajoutez des blocs pour composer votre page.</p>}
              {form.blocks.map((b) => <div key={b.id}><BlockRenderer block={b} /></div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContentTab() {
  const [fields, setFields] = useState(null);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);

  const load = () => api.get("/admin/cms/content").then(({ data }) => { if (!dirtyRef.current) setFields(data); }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const setVal = (key, val) => { dirtyRef.current = true; setFields((f) => ({ ...f, [key]: { ...f[key], draft: val } })); };

  const save = async (publish) => {
    setSaving(true);
    try {
      const values = {};
      Object.entries(fields).forEach(([k, v]) => { values[k] = v.draft; });
      await api.put("/admin/cms/content", { values });
      if (publish) await api.post("/admin/cms/content/publish");
      toast.success(publish ? "Modifications publiées ✅" : "Brouillon enregistré");
      dirtyRef.current = false;
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  if (!fields) return <p className="text-slate-400">Chargement…</p>;

  const LABELS = {
    "home.hero.eyebrow": "Accueil — petite étiquette (au-dessus du titre)",
    "home.hero.subtitle": "Accueil — sous-titre principal",
    "home.hero.cta": "Accueil — texte du bouton principal",
    "partner.hero.subtitle": "Devenir partenaire — sous-titre d'intro",
    "about.intro": "À propos — paragraphe d'introduction",
    "home.hero.image": "Accueil — image de héros (fond de la bannière)",
    "partner.hero.image": "Devenir partenaire — image de héros (fond de la bannière)",
  };

  return (
    <div className="max-w-2xl" data-testid="cms-content-tab">
      <p className="text-sm text-slate-500 mb-4">Modifiez les textes et images des pages existantes. Enregistrez en brouillon, puis publiez pour les rendre visibles.</p>
      <div className="space-y-4">
        {Object.entries(fields).map(([key, v]) => {
          const isImage = key.endsWith(".image");
          return (
            <div key={key} className="bg-white rounded-2xl border border-slate-200 p-4">
              <Label className="text-sm font-semibold text-slate-800">{LABELS[key] || key}</Label>
              {isImage ? (
                <div className="mt-2 space-y-2">
                  {v.draft ? (
                    <img src={v.draft} alt="" className="h-32 w-full max-w-sm rounded-lg border border-slate-200 object-cover" data-testid={`cms-content-img-preview-${key}`} />
                  ) : (
                    <div className="h-32 w-full max-w-sm rounded-lg border border-dashed border-slate-300 grid place-items-center text-xs text-slate-400">Aucune image — dégradé par défaut</div>
                  )}
                  <div className="flex gap-2">
                    <UploadBtn onUploaded={(url) => setVal(key, url)} testId={`cms-content-upload-${key}`} />
                    {v.draft && <Button type="button" variant="outline" size="sm" onClick={() => setVal(key, "")} data-testid={`cms-content-clear-${key}`}><Trash2 size={15} className="mr-1.5" /> Retirer</Button>}
                  </div>
                </div>
              ) : (
                <Textarea rows={2} className="mt-1" value={v.draft} onChange={(e) => setVal(key, e.target.value)} data-testid={`cms-content-${key}`} />
              )}
              {v.draft !== v.published && <p className="mt-1 text-xs text-amber-600">Modifié (non publié)</p>}
            </div>
          );
        })}
      </div>
      <div className="mt-5 flex gap-2">
        <Button variant="outline" disabled={saving} onClick={() => save(false)} data-testid="cms-content-save-draft"><Save size={15} className="mr-1.5" /> Enregistrer le brouillon</Button>
        <Button disabled={saving} onClick={() => save(true)} className="bg-[#E05638] text-white" data-testid="cms-content-publish"><Rocket size={15} className="mr-1.5" /> Publier</Button>
      </div>
    </div>
  );
}

function SocialImageTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get("/admin/site-settings").then(({ data }) => setSettings(data || {})).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const save = async (nextUrl) => {
    setSaving(true);
    try {
      const { id, updated_at, _id, ...payload } = settings || {}; // eslint-disable-line
      payload.og_image_url = nextUrl;
      await api.put("/admin/site-settings", payload);
      setSettings((s) => ({ ...s, og_image_url: nextUrl }));
      toast.success(nextUrl ? "Image d'aperçu enregistrée ✅" : "Image d'aperçu réinitialisée");
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  if (!settings) return <p className="text-slate-400">Chargement…</p>;
  const current = settings.og_image_url || "";

  return (
    <div className="max-w-2xl" data-testid="cms-social-tab">
      <p className="text-sm text-slate-500 mb-4">
        Cette image s'affiche en aperçu lorsque vous (ou vos visiteurs) partagez la page d'accueil de PROXIreduc-28
        sur Facebook, WhatsApp, LinkedIn, etc. Format conseillé : <strong>1200 × 630 px</strong> (paysage).
        Après l'avoir changée, pensez à « re-collecter » l'aperçu dans le{" "}
        <a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noreferrer" className="text-[#E05638] underline">débogueur Facebook</a>.
      </p>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <Label className="text-sm font-semibold text-slate-800">Image d'aperçu réseaux sociaux (Open Graph)</Label>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <img
            src={current || `${backend}/api/site/og-image`}
            alt="Aperçu réseaux sociaux"
            className="w-full max-w-md aspect-[1200/630] object-cover rounded-lg border border-slate-200 bg-white"
            data-testid="social-og-preview"
          />
          <p className="mt-2 text-xs text-slate-400">
            {current ? "Image personnalisée active." : "Image par défaut du site (aucune image personnalisée définie)."}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <UploadBtn onUploaded={(url) => save(url)} label="Téléverser une nouvelle image" testId="social-og-upload" />
          {current && (
            <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => save("")} data-testid="social-og-reset">
              <Trash2 size={15} className="mr-1.5" /> Rétablir l'image par défaut
            </Button>
          )}
        </div>

        <div className="pt-1">
          <Label className="text-xs text-slate-500">…ou coller l'URL d'une image</Label>
          <div className="flex gap-2 mt-1">
            <Input
              className="h-9"
              value={current}
              onChange={(e) => setSettings((s) => ({ ...s, og_image_url: e.target.value }))}
              placeholder="https://…/mon-image.jpg"
              data-testid="social-og-url"
            />
            <Button type="button" size="sm" disabled={saving} onClick={() => save(settings.og_image_url || "")} className="bg-[#E05638] text-white" data-testid="social-og-save">
              <Save size={15} className="mr-1.5" /> Enregistrer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminSiteBuilder() {
  const [tab, setTab] = useState("pages");
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null); // page object or null

  const load = () => api.get("/admin/cms/pages").then(({ data }) => setPages(data)).catch((e) => toast.error(formatApiError(e)));
  const [pageTemplates, setPageTemplates] = useState([]);
  const loadPageTemplates = () => api.get("/admin/cms/templates?kind=page").then(({ data }) => setPageTemplates(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { loadPageTemplates(); }, []);

  const del = async (p) => {
    if (!window.confirm(`Supprimer la page « ${p.title} » ?`)) return;
    try { await api.delete(`/admin/cms/pages/${p.id}`); toast.success("Page supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const duplicate = async (p) => {
    try {
      const full = (await api.get(`/admin/cms/pages/${p.id}`)).data;
      await api.post("/admin/cms/pages", { title: `${full.title} (copie)`, blocks: full.blocks || [],
        meta_description: full.meta_description || "", nav_show: false, status: "draft" });
      toast.success("Page dupliquée (brouillon)");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePublish = async (p) => {
    try {
      await api.post(`/admin/cms/pages/${p.id}/${p.status === "published" ? "unpublish" : "publish"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const importRoute = async (route) => {
    try {
      const { data } = await api.post("/admin/cms/pages/import-route", { route });
      toast.success("Copie éditable créée. Personnalisez puis activez le remplacement et publiez.");
      setEditing(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing) {
    return <div data-testid="admin-site-builder"><BuilderEditor page={editing} onBack={() => { setEditing(null); load(); }} onSaved={() => load()} /></div>;
  }

  return (
    <div data-testid="admin-site-builder">
      <div className="flex items-center gap-2 mb-6">
        <Layout className="text-[#E05638]" size={24} />
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">Éditeur de site</h1>
      </div>

      <div className="inline-flex rounded-xl bg-slate-100 p-1 mb-6">
        <button onClick={() => setTab("pages")} data-testid="cms-tab-pages" className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${tab === "pages" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Pages</button>
        <button onClick={() => setTab("content")} data-testid="cms-tab-content" className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${tab === "content" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Contenu du site</button>
        <button onClick={() => setTab("apparence")} data-testid="cms-tab-apparence" className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${tab === "apparence" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Apparence</button>
        <button onClick={() => setTab("layout")} data-testid="cms-tab-layout" className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${tab === "layout" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>En-tête & pied</button>
        <button onClick={() => setTab("social")} data-testid="cms-tab-social" className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${tab === "social" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Aperçu réseaux sociaux</button>
      </div>

      {tab === "content" ? <ContentTab /> : tab === "apparence" ? <ThemeTab /> : tab === "layout" ? <LayoutTab /> : tab === "social" ? <SocialImageTab /> : (
        <>
          <div className="mb-5 bg-white rounded-2xl border border-slate-200 p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Créer une page</div>
            <div className="flex flex-wrap gap-2">
              {TEMPLATES.map((t) => (
                <button key={t.key} onClick={() => setEditing({ title: t.title, blocks: t.build() })}
                  data-testid={t.key === "blank" ? "cms-new-page" : `cms-template-${t.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition ${t.key === "blank" ? "bg-[#E05638] text-white" : "border border-slate-200 text-slate-700 hover:border-[#E05638] hover:text-[#E05638]"}`}>
                  <Plus size={14} /> {t.label}
                </button>
              ))}
            </div>
            {pageTemplates.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100" data-testid="cms-page-templates">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Depuis un de mes modèles de page</div>
                <div className="flex flex-wrap gap-2">
                  {pageTemplates.map((t) => (
                    <button key={t.id} onClick={() => setEditing({ title: `${t.name}`, blocks: t.data || [] })}
                      data-testid={`cms-page-template-${t.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-slate-200 text-slate-700 hover:border-[#E05638] hover:text-[#E05638] transition">
                      <Copy size={14} /> {t.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="mb-5 bg-white rounded-2xl border border-orange-200 p-4" data-testid="cms-migrate-routes">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1">Remplacer une page existante du site</div>
            <p className="text-xs text-slate-500 mb-3">Crée une copie éditable de la page officielle. Vous la personnalisez, puis l'activez à sa place — le code métier reste intact.</p>
            <div className="flex flex-wrap gap-2">
              {OVERRIDABLE_ROUTES.map((r) => (
                <button key={r.route} onClick={() => importRoute(r.route)} data-testid={`cms-import-route-${r.route === "/" ? "home" : r.route.replace(/\//g, "")}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-orange-200 text-orange-700 hover:bg-orange-50 transition">
                  <Copy size={14} /> {r.label}
                </button>
              ))}
            </div>
          </div>
          {pages.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400" data-testid="cms-empty">
              <FileText size={32} className="mx-auto mb-2 opacity-50" />
              Aucune page pour l'instant. Créez votre première page avec le bouton ci-dessus.
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="cms-pages-list">
              {pages.map((p) => (
                <div key={p.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`cms-page-row-${p.slug}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 truncate">{p.title}</div>
                    <div className="text-xs text-slate-400">/p/{p.slug}</div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {p.status === "published" ? "Publiée" : "Brouillon"}
                  </span>
                  {p.route && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.override_active ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-400"}`} data-testid={`cms-override-badge-${p.slug}`}>
                      {p.override_active ? `Remplace ${p.route}` : `Copie de ${p.route}`}
                    </span>
                  )}
                  {p.status === "published" && (
                    <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Voir" data-testid={`cms-view-${p.slug}`}><Eye size={16} /></a>
                  )}
                  <button onClick={() => togglePublish(p)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title={p.status === "published" ? "Dépublier" : "Publier"} data-testid={`cms-toggle-${p.slug}`}><Globe size={16} /></button>
                  <button onClick={() => duplicate(p)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Dupliquer" data-testid={`cms-duplicate-${p.slug}`}><Copy size={16} /></button>
                  <button onClick={() => setEditing(p)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Modifier" data-testid={`cms-edit-${p.slug}`}><Pencil size={16} /></button>
                  <button onClick={() => del(p)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" title="Supprimer" data-testid={`cms-delete-${p.slug}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
